import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { nav } from "../data/content";
import { Menu, X } from "lucide-react";
import BrandLogo from "./BrandLogo";

const Navbar = () => {
  const { isAr, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 2.5 }}
      className="fixed top-0 inset-x-0 z-50 transition-all duration-700"
      style={{
        background: scrolled ? "#0F2F29" : "transparent",
        backdropFilter: scrolled ? "none" : "none",
        WebkitBackdropFilter: scrolled ? "none" : "none",
        borderBottom: scrolled
          ? "1px solid rgba(232,211,195,0.18)"
          : "1px solid transparent",
      }}
      data-testid="navbar"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-[72px]">
          {/* Brand */}
          <button
            onClick={() => scrollTo("home")}
            className="flex items-center cursor-hover"
            data-testid="nav-brand"
          >
            <BrandLogo height={26} theme="light" />
          </button>

          {/* Center menu */}
          <div className="hidden lg:flex items-center gap-8">
            {nav.links.slice(1, -1).map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className={`${isAr ? "label-meta-ar" : "label-meta"} transition-colors cursor-hover ${
                  scrolled
                    ? "text-bg-ivory/85 hover:text-champagne"
                    : "text-bg-ivory/75 hover:text-bg-ivory"
                }`}
                data-testid={`nav-${l.id}`}
                style={{ fontSize: isAr ? 13 : 11 }}
              >
                {isAr ? l.ar : l.en}
              </button>
            ))}
          </div>

          {/* Right: lang toggle + CTA */}
          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={toggle}
              className={`label-meta transition-colors cursor-hover ${
                scrolled ? "text-bg-ivory hover:text-champagne" : "text-bg-ivory/85 hover:text-bg-ivory"
              }`}
              data-testid="lang-toggle"
            >
              {isAr ? "EN" : "AR"}
            </button>
            <button
              onClick={() => scrollTo("contact")}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.22em] transition-all duration-500 cursor-hover"
              style={{
                background: scrolled ? "#E8D3C3" : "transparent",
                color: scrolled ? "#173F35" : "#F4F1EA",
                border: scrolled ? "1px solid transparent" : "1px solid rgba(244,241,234,0.45)",
              }}
              data-testid="contact-cta-nav"
            >
              {t("ابدأ مشروعك", "Start Project")}
              <span>→</span>
            </button>

            <button
              onClick={() => setOpen((s) => !s)}
              className={`lg:hidden ${scrolled ? "text-bg-ivory" : "text-bg-ivory"}`}
              aria-label="menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0F2F29]"
            style={{ borderTop: "1px solid rgba(232,211,195,0.15)" }}
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {nav.links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-bg-ivory text-lg text-start cursor-hover"
                  data-testid={`nav-mobile-${l.id}`}
                >
                  {isAr ? l.ar : l.en}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
